/**
 * Inserção de mídia na timeline.
 *
 * Centralizado porque três lugares inserem — painel de mídia, biblioteca e arrastar —
 * e os três precisam das mesmas três decisões: em que tipo de track vai, o que fazer
 * quando não há track livre naquele instante, e o que precisa viajar no `meta`.
 *
 * Quando não há track livre, cria uma. É deliberado: um efeito sonoro inserido meio
 * segundo depois de outro NÃO pode sobrescrever o primeiro, e `ADD_ITEM` em modo
 * `overwrite` faria exatamente isso. A track nova e o item vão no MESMO comando `BATCH`,
 * então é um único passo de undo — o usuário desfaz "inseri um som", não "criei uma
 * track" e depois "inseri um som".
 */
import {
  build,
  createItem,
  newTrack,
  placementTrack,
  type Command,
  type ItemKind,
  type Sequence,
  type TrackKind,
  type TimelineItem,
} from '@easyedit/timeline'

export interface InsertRequest {
  assetId: string
  /** Tipo da mídia, já resolvido para o vocabulário da timeline. */
  itemKind: ItemKind
  trackKind: TrackKind
  start: number
  duration: number
  label: string
  volume?: number
  fadeIn?: number
  fadeOut?: number
  /** Viaja junto com o item: bpm/beats/bars de trilha, origem na biblioteca. */
  meta?: Record<string, unknown>
}

export interface InsertResult {
  command: Command
  item: TimelineItem
  /** Preenchido quando foi preciso criar uma track para caber. */
  createdTrack?: { id: string; name: string }
}

export function buildInsert(sequence: Sequence, request: InsertRequest): InsertResult {
  const ctx = { sequenceId: sequence.id }
  const end = request.start + request.duration
  const existing = placementTrack(sequence, request.trackKind, request.start, end)

  const item = createItem({
    kind: request.itemKind,
    start: request.start,
    duration: request.duration,
    // Mídia com duração própria entra inteira; imagem e elemento não têm `out` real,
    // mas manter o par in/out coerente evita ramo especial no compilador de render.
    source: { assetId: request.assetId, in: 0, out: request.duration },
    label: request.label,
    meta: request.meta,
  })
  if (request.volume !== undefined) item.volume = request.volume
  if (request.fadeIn !== undefined) item.fadeIn = request.fadeIn
  if (request.fadeOut !== undefined) item.fadeOut = request.fadeOut

  if (existing) {
    return { command: build.addItem(ctx, existing.id, item), item }
  }

  const track = newTrack(sequence.tracks, request.trackKind)
  return {
    command: build.batch(ctx, [build.addTrack(ctx, track), build.addItem(ctx, track.id, item)]),
    item,
    createdTrack: { id: track.id, name: track.name },
  }
}

/** Onde cada tipo de mídia mora. */
export function trackKindFor(itemKind: ItemKind, hint?: 'music' | 'sfx' | 'overlay'): TrackKind {
  if (hint) return hint
  if (itemKind === 'audio') return 'audio'
  if (itemKind === 'text') return 'text'
  if (itemKind === 'caption') return 'caption'
  return 'video'
}
